import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Table, Button, Input, Select, message, Modal, Form, AutoComplete, InputNumber, Typography, Switch, DatePicker, Menu, Dropdown, Checkbox, Tooltip, Divider, Tabs, Spin } from 'antd';

import { Box, Paper } from '@mui/material';
import { 
  DownloadOutlined, 
  PlusOutlined, 
  SearchOutlined, 
  FileTextOutlined, 
  EditOutlined, 
  InfoCircleOutlined, 
  DownOutlined, 
  MailOutlined,
  FilePdfOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import api from "../../../authentication/axiosInstance";
import { ArchiveWithAllocationsButton, UnarchiveButton } from '../../../components/allocation/ArchiveWithAllocationsModal';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { generateSiteDetailsPDF } from '../../../utils/pdfUtils';
import { IconFile } from '@tabler/icons-react';
import styles from './SitesListView.module.css';
import { useJsApiLoader } from '@react-google-maps/api';

const { Option } = Select;
const { Title } = Typography;

const libraries = ['places'];

const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const CONTRACT_STATUS_RANK = {
  Confirmed: 0,
  Unconfirmed: 1,
  Estimate: 2,
};

const getApiErrorMessage = (value) => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map(getApiErrorMessage).find(Boolean);
  }
  if (value && typeof value === 'object') {
    return Object.values(value).map(getApiErrorMessage).find(Boolean);
  }
  return null;
};

const STATUS_DEALLOCATION_LABELS = {
  loader_site: 'Loader site',
  equipment_site: 'Equipment site',
  loader_staff: 'Loader staff',
  walkway_site: 'Walkway Crew site',
  salting_site: 'Salting Route site',
  site_state: 'Finalization state',
};

const statusDeallocationImpact = (preview) => {
  const breakdown = Object.entries(STATUS_DEALLOCATION_LABELS)
    .filter(([key]) => preview.counts?.[key])
    .map(([key, label]) => `${label}: ${preview.counts[key]}`)
    .join(' · ') || 'No existing allocations will be removed.';
  const liveOnly = preview.live_only_allocation_count > 0
    ? ` This includes ${preview.live_only_allocation_count} additional live-only allocations.`
    : '';
  const retainedCount = preview.retained_shared_loader_staff_assignment_count ?? preview.shared_loader_resources_retained ?? 0;
  const retained = ` Shared loader staff assignments left in place: ${retainedCount}${retainedCount > 0 ? ' because those vehicles still serve another Confirmed site' : ''}.`;
  const locked = ` Allocations preserved in locked history: ${preview.locked_allocation_count || 0} across ${preview.locked_setup_count || 0} locked setups.`;
  const setups = preview.setups?.length > 0
    ? ` Affected setups: ${preview.setups.map((setup) => setup.setup_name).join(', ')}.`
    : '';
  return `This will remove ${preview.allocation_count} loader, equipment, staff, route, or finalization allocations across ${preview.setup_count} editable setups for ${preview.transitioning_site_count} Confirmed sites. Removed from editable/live planning: ${preview.allocation_count}. ${breakdown}.${liveOnly}${retained}${locked} Walkway Crew and Salting Route site allocations will be removed from affected editable setups. Plows and DoubleDowns will remain assigned to their vehicles. Unsaved dispatch boards containing these sites will be deleted. Saved dispatch boards will remain with the site removed from their current associations; dispatch history and unrelated board associations will remain.${setups}`;
};

const confirmStatusDeallocation = (preview, title) => new Promise((resolve) => {
  Modal.confirm({
    title,
    content: statusDeallocationImpact(preview),
    okText: 'Apply changes',
    cancelText: 'Cancel',
    onOk: () => resolve(preview.preview_hash),
    onCancel: () => resolve(null),
  });
});

export const getSiteCoverageValidationError = (value) => {
  if (value === '' || value === undefined || value === null) {
    return 'Please enter the site coverage';
  }
  const coverage = Number(value);
  if (!Number.isInteger(coverage)) {
    return 'Site coverage must be a whole number';
  }
  if (coverage < 0 || coverage > 100) {
    return 'Site coverage must be between 0 and 100';
  }
  return null;
};

const compareText = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

export const compareSitesForManagement = (a, b) => {
  const statusCompare = (CONTRACT_STATUS_RANK[a.contract_status] ?? 99) - (CONTRACT_STATUS_RANK[b.contract_status] ?? 99);
  if (statusCompare !== 0) return statusCompare;

  const clientCompare = compareText(a.client_name, b.client_name);
  if (clientCompare !== 0) return clientCompare;

  const siteCompare = compareText(a.site_name, b.site_name);
  if (siteCompare !== 0) return siteCompare;

  return (a.site_id || 0) - (b.site_id || 0);
};

export const sortSitesForManagement = (siteList) => [...siteList].sort(compareSitesForManagement);

export const filterSitesForManagement = (
  siteList,
  { searchText = '', searchRegionFilter = null, searchStatusFilter = null, searchClientFilter = '' } = {}
) => sortSitesForManagement(
  siteList.filter((site) => {
    const filterText = (site.site_name || '').toLowerCase().includes(searchText.toLowerCase());
    const filterSiteRegion = searchRegionFilter ? site.region === searchRegionFilter : true;
    const filterSiteStatus = searchStatusFilter ? site.contract_status === searchStatusFilter : true;
    const filterTextClient = (site.client_name || '').toLowerCase().includes(searchClientFilter.toLowerCase());
    return filterText && filterSiteRegion && filterSiteStatus && filterTextClient;
  })
);

const useDebouncedValue = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [value, delay]);

  return debouncedValue;
};


const SitesListView = () => {
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [siteSearchInput, setSiteSearchInput] = useState('');
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 10 });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [viewMode, setViewMode] = useState('default');
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [editForm] = Form.useForm();
  const [bulkForm] = Form.useForm();
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [isBulkEditModalVisible, setIsBulkEditModalVisible] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isClientDetailsModalVisible, setIsClientDetailsModalVisible] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isAddingNewClient, setIsAddingNewClient] = useState(false);

  const [searchRegionFilter, setSearchRegionFilter] = useState(null);
  const [searchStatusFilter, setSearchStatusFilter] = useState(null);
  const searchText = useDebouncedValue(siteSearchInput, 300);
  const searchClientFilter = useDebouncedValue(clientSearchInput, 300);

  const [selectedAddress, setSelectedAddress] = useState('');
  const [selectedEditAddress, setSelectedEditAddress] = useState('');
  const [selectedLatitude, setSelectedLatitude] = useState('');
  const [selectedLongitude, setSelectedLongitude] = useState('');
  const [selectedEditLatitude, setSelectedEditLatitude] = useState('');
  const [selectedEditLongitude, setSelectedEditLongitude] = useState('');

  const [territoryManagers, setTerritoryManagers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [weatherStations, setWeatherStations] = useState([]);
  const [archiveTab, setArchiveTab] = useState('active');
  const updateBulkRegion = Form.useWatch('update_region', bulkForm);
  const updateBulkStatus = Form.useWatch('update_contract_status', bulkForm);
  const updateBulkTerritoryManager = Form.useWatch('update_assigned_territory_manager', bulkForm);
  const hasBulkFieldEnabled = Boolean(
    updateBulkRegion || updateBulkStatus || updateBulkTerritoryManager
  );

  const fetchStaff = async () => {
    try {
      const response = await api.get('/staff/');
      setStaffList(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      message.error('Failed to fetch staff');
    }
  };

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites/management_list/', {
        params: { archived: archiveTab === 'archived' },
      });
      const sortedSites = sortSitesForManagement(response.data || []);
      setSites(sortedSites);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      message.error('Failed to fetch sites');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients/');
      const clientData = Array.isArray(response.data) ? response.data : [];
      setClients(clientData);
      console.log(clients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      message.error('Failed to fetch clients');
    }
  };
  
  const fetchTerritoryManagers = async () => {
    try {
      const response = await api.get('/staff/territory_manager_staff/');
      setTerritoryManagers(response.data);
    } catch (error) {
      console.error('Failed to fetch Territory Managers:', error);
      message.error('Failed to fetch Territory Managers');
    }
  };

  const fetchWeatherStations = async () => {
    try {
      const response = await api.get('/weather-stations/');
      setWeatherStations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch weather stations:', error);
      message.error('Failed to fetch weather stations');
    }
  };
  

  useEffect(() => {
    fetchSites();
    fetchClients();
    fetchStaff();
    fetchTerritoryManagers();
    fetchWeatherStations();
  }, [archiveTab]);

  const filteredSites = useMemo(
    () => filterSitesForManagement(sites, {
      searchText,
      searchRegionFilter,
      searchStatusFilter,
      searchClientFilter,
    }),
    [sites, searchText, searchRegionFilter, searchStatusFilter, searchClientFilter]
  );

  const regionOptions = useMemo(
    () => Array.from(new Set(sites.map((site) => site.region).filter(Boolean))),
    [sites]
  );

  const statusOptions = useMemo(
    () => Array.from(new Set(sites.map((site) => site.contract_status).filter(Boolean))),
    [sites]
  );

  useEffect(() => {
    setTablePagination((currentPagination) => ({
      ...currentPagination,
      current: 1,
    }));
  }, [searchText, searchClientFilter, searchRegionFilter, searchStatusFilter]);

  useEffect(() => {
    setTablePagination((currentPagination) => {
      const maxPage = Math.max(1, Math.ceil(filteredSites.length / currentPagination.pageSize));
      if (currentPagination.current <= maxPage) {
        return currentPagination;
      }
      return {
        ...currentPagination,
        current: maxPage,
      };
    });
  }, [filteredSites.length, tablePagination.pageSize]);

  useEffect(() => {
    const availableSiteIds = new Set(filteredSites.map((site) => site.site_id));
    setSelectedSiteIds((currentIds) => {
      const nextIds = currentIds.filter((siteId) => availableSiteIds.has(siteId));
      return nextIds.length === currentIds.length ? currentIds : nextIds;
    });
  }, [filteredSites]);

  useEffect(() => {
    setSelectedSiteIds([]);
    setIsBulkEditModalVisible(false);
    bulkForm.resetFields();
  }, [archiveTab, bulkForm]);

  const handleCheckboxChange = (record, field, newValue) => {
    Modal.confirm({
      title: `Are you sure you want to make this change?`,
      onOk: async () => {
        try {
          const updatedData = { [field]: newValue };
  
          const response = await api.patch(`/sites/${record.site_id}/`, updatedData);
          fetchSites();
          if (response.status === 200 || response.status === 204) {
            message.success(`Field updated successfully`);
  
            setSites((prevSites) =>
              prevSites.map((site) =>
                site.site_id === record.site_id ? { ...site, [field]: newValue } : site
              )
            );
          } else {
            console.error('Unexpected response:', response);
            message.error(`Failed to update ${field.replace('_', ' ')}`);
          }
        } catch (error) {
          console.error('Update error:', error);
          message.error(`Failed to update ${field.replace('_', ' ')}`);
        }
      },
      onCancel() {
        message.info('No changes were made');
      },
    });
  };

  const defaultColumns = [
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      width: 200,
      sorter: (a, b) => a.client_name.localeCompare(b.client_name),
      render: (text, record) => (
        <span>
          {text}
          <Tooltip title="View Client Details">
            <InfoCircleOutlined
              onClick={() => showClientDetailsModal(record)}
              className={styles.actionIcon}
              style={{ marginLeft: 8 }}
            />
          </Tooltip>
        </span>
      ),
    },
    {
      title: 'Site Name',
      dataIndex: 'site_name',
      key: 'site_name',
      width: 200,
      sorter: (a, b) => a.site_name.localeCompare(b.site_name),
    },
    {
      title: 'Contract Status',
      dataIndex: 'contract_status',
      key: 'contract_status',
      width: 150,
      sorter: compareSitesForManagement,
      render: (status, record) => (
        <Select
          value={status}
          style={{ width: 140 }}
          onChange={(value) => handleContractStatusChange(record.site_id, value)}
        >
          <Option value="Estimate">Estimate</Option>
          <Option value="Unconfirmed">Unconfirmed</Option>
          <Option value="Confirmed">Confirmed</Option>
        </Select>
      ),
    },
    {
      title: 'Territory Manager',
      dataIndex: 'assigned_territory_manager',
      key: 'assigned_territory_manager',
      width: 180,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <div className={styles.actionsCell}>
          {archiveTab === 'active' && (
            <>
              <Tooltip title="Edit Site">
                <EditOutlined
                  onClick={() => handleEdit(record)}
                  className={styles.actionIcon}
                />
              </Tooltip>
              <Tooltip title="Generate Invoice">
                <FileTextOutlined
                  onClick={() => handleGenerateInvoice(record)}
                  className={styles.actionIcon}
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Download PDF">
            <FilePdfOutlined
              onClick={() => handleDownloadPDF(record)}
              className={styles.actionIcon}
            />
          </Tooltip>
          {archiveTab === 'active' ? (
            <ArchiveWithAllocationsButton
              itemType="site"
              itemId={record.site_id}
              itemLabel={record.site_name}
              onArchived={fetchSites}
              buttonProps={{ size: 'small' }}
            />
          ) : (
            <UnarchiveButton
              itemType="site"
              itemId={record.site_id}
              itemLabel={record.site_name}
              onUnarchived={fetchSites}
              buttonProps={{ size: 'small' }}
            />
          )}
        </div>
      ),
    },
  ];

  const contractDetailsColumns = [
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      sorter: (a, b) => a.client_name.localeCompare(b.client_name),
    },
    {
      title: 'Site Name',
      dataIndex: 'site_name',
      key: 'site_name',
      sorter: (a, b) => a.site_name.localeCompare(b.site_name),
    },
    {
      title: 'Contract Status',
      dataIndex: 'contract_status',
      key: 'contract_status',
      sorter: compareSitesForManagement,
      render: (status, record) => (
        <Select
          value={status}
          style={{ width: 140 }}
          onChange={(value) => handleContractStatusChange(record.site_id, value)}
        >
          <Option value="Estimate">Estimate</Option>
          <Option value="Unconfirmed">Unconfirmed</Option>
          <Option value="Confirmed">Confirmed</Option>
        </Select>
      ),
    },
    {
      title: 'Base Revenue',
      dataIndex: 'base_revenue',
      key: 'base_revenue',
      render: (value) => `$${formatNumber(value)}`,
    },
    {
      title: 'Winter Start Date',
      dataIndex: 'winter_start_date',
      key: 'winter_start_date',
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : 'N/A',
    },
    {
      title: 'Winter End Date',
      dataIndex: 'winter_end_date',
      key: 'winter_end_date',
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : 'N/A',
    },
    {
      title: 'Contract Expiration',
      dataIndex: 'contract_expiration_date',
      key: 'contract_expiration_date',
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : 'N/A',
    },
    {
      title: 'Monthly Rate (No HST)',
      dataIndex: 'monthly_rate',
      key: 'monthly_rate',
      render: (value) => `$${formatNumber(value)}`,
    },
    {
      title: 'Additional Revenue Type',
      dataIndex: 'additional_revenue_type',
      key: 'additional_revenue_type',
    },
    {
      title: 'Additional Revenue Amount',
      dataIndex: 'additional_revenue_amount',
      key: 'additional_revenue_amount',
      render: (value) => `$${formatNumber(value)}`,
    },
  ];

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedAddress('');
    setSelectedLatitude('');
    setSelectedLongitude('');
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      // Manually validate all fields first
      const values = await form.validateFields();
      
      // Check if address is in form values
      if (!values.address || !values.latitude || !values.longitude) {
        message.error('Please select a valid address.');
        return;
      }

      let clientId = values.client;
      let clientName = '';

      // If adding a new client, create contact and client first
      if (values.client === 'new' && isAddingNewClient) {
        try {
          // First create the client
          const newClientData = {
            client_name: values.new_client_name
          };

          // Create client
          const clientResponse = await api.post('/clients/', newClientData);
          clientId = clientResponse.data.client_id;
          clientName = clientResponse.data.client_name;

          // Then create the contact through the client's contacts endpoint
          const contactData = {
            first_name: values.contact_first_name,
            last_name: values.contact_last_name,
            email: values.contact_email,
            phone: values.contact_phone,
            notes: '',
            make_primary: true
          };

          // Create contact and associate it with the client
          await api.post(`/clients/${clientId}/contacts/`, contactData);

          message.success('New client and contact created successfully');
        } catch (error) {
          console.error('Failed to create new client/contact:', error);
          if (error.response?.data) {
            const errorMessages = Object.entries(error.response.data)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');
            message.error(`Failed to create client/contact: ${errorMessages}`);
          } else {
            message.error('Failed to create new client/contact. Please check your input and try again.');
          }
          return;
        }
      } else {
        // Get client name for existing client
        clientName = (clients || []).find((client) => client.client_id === values.client)?.client_name || null;
      }

      const formattedValues = {
        ...values,
        client: clientId,
        client_name: clientName,
        parking_lots_sqft: parseFloat(values.parking_lots_sqft),
        walkway_sqft: parseFloat(values.walkway_sqft),
        site_coverage: parseInt(values.site_coverage, 10),
        latitude: parseFloat(parseFloat(values.latitude).toFixed(6)),
        longitude: parseFloat(parseFloat(values.longitude).toFixed(6)),
        contract_status: values.contract_status,
        base_revenue: parseFloat(values.base_revenue),
        winter_start_date: values.winter_start_date ? values.winter_start_date.format('YYYY-MM-DD') : null,
        winter_end_date: values.winter_end_date ? values.winter_end_date.format('YYYY-MM-DD') : null,
        contract_expiration_date: values.contract_expiration_date ? values.contract_expiration_date.format('YYYY-MM-DD') : null,
        monthly_rate: parseFloat(values.monthly_rate),
        additional_revenue_type: values.additional_revenue_type,
        additional_revenue_amount: parseFloat(values.additional_revenue_amount),
      };

      // Remove the new client fields from the site creation payload
      delete formattedValues.new_client_name;
      delete formattedValues.contact_first_name;
      delete formattedValues.contact_last_name;
      delete formattedValues.contact_email;
      delete formattedValues.contact_phone;

      console.log('Formatted form values before submission:', formattedValues);
      const response = await api.post('/sites/', formattedValues);
      console.log('API response:', response.data);
      message.success('Site added successfully');
      setIsModalVisible(false);
      form.resetFields();
      setIsAddingNewClient(false);
      fetchSites();
      fetchClients(); // Refresh clients list
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error('Failed to add site:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        if (typeof error.response.data === 'object' && error.response.data !== null) {
          const errorMessages = Object.entries(error.response.data)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
          message.error(`Failed to add site: ${errorMessages}`);
        } else {
          message.error('Failed to add site. Please check your input and try again.');
        }
      } else if (error.request) {
        console.error('Error request:', error.request);
        message.error('Network error. Please check your connection and try again.');
      } else {
        message.error('An unexpected error occurred. Please try again.');
      }
    }
  };

  // removed legacy extractStreetAddress / geocode helpers – now handled by google maps Autocomplete's place geometry inside onPlaceChanged

  const validateNumber = (rule, value) => {
    if (value === '' || value === undefined) {
      return Promise.reject('Please enter a value');
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return Promise.reject('Please enter a valid number');
    }
    if (num < 0) {
      return Promise.reject('Please enter a positive number');
    }
    return Promise.resolve();
  };

  const validateSiteCoverage = (rule, value) => {
    const validationError = getSiteCoverageValidationError(value);
    return validationError ? Promise.reject(validationError) : Promise.resolve();
  };

  const runStatusChangeWithPreview = async ({
    siteIds,
    targetStatus,
    title,
    mutate,
  }) => {
    let previewResponse = await api.post('/sites/status-deallocation-preview/', {
      site_ids: siteIds,
      target_status: targetStatus,
    });
    let preview = previewResponse.data;

    while (preview?.transitioning_site_count > 0) {
      const previewHash = await confirmStatusDeallocation(preview, title);
      if (!previewHash) return null;
      try {
        return await mutate(previewHash);
      } catch (error) {
        if (error.response?.status === 409 && error.response?.data?.preview) {
          preview = error.response.data.preview;
          message.warning('Allocation impact changed. Review and confirm again.');
          continue;
        }
        throw error;
      }
    }
    return mutate(null);
  };

  const handleContractStatusChange = async (siteId, newStatus) => {
    try {
      const currentSite = sites.find((site) => site.site_id === siteId);
      const mutation = (previewHash) => api.patch(`/sites/${siteId}/`, {
        contract_status: newStatus,
        ...(previewHash ? { status_preview_hash: previewHash } : {}),
      });
      const response = (
        currentSite?.contract_status === 'Confirmed' &&
        newStatus !== 'Confirmed'
      )
        ? await runStatusChangeWithPreview({
          siteIds: [siteId],
          targetStatus: newStatus,
          title: `Change ${currentSite.site_name} to ${newStatus}?`,
          mutate: mutation,
        })
        : await mutation(null);
      if (!response) return;
      message.success('Contract status updated successfully');
      setSites((prevSites) => {
        return sortSitesForManagement(
          prevSites.map((site) =>
            site.site_id === siteId ? { ...site, contract_status: newStatus } : site
          )
        );
      });
    } catch (error) {
      console.error('Failed to update contract status:', error);
      message.error('Failed to update contract status');
    }
  };

  const handleTableChange = (nextPagination) => {
    setTablePagination({
      current: nextPagination.current || 1,
      pageSize: nextPagination.pageSize || 10,
    });
  };

  const handleGenerateInvoices = async (siteId) => {
    try {
      await api.post(`/sites/${siteId}/generate_invoice/`);
      message.success('Invoice generated successfully');
      fetchSites(); // Refresh the sites list to update the invoice status
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      message.error('Failed to generate invoice');
    }
  };

  const generateManagementPDF = async () => {
    try {
      const response = await api.get('/sites/site_supervision_pdf/', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, 'site_supervision_report.pdf');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      message.error('Failed to generate PDF report');
    }
  };

  const generateManagerPDF = async (territoryManager) => {
    if (!territoryManager) {
      console.error('Territory manager is required');
      message.error('Please select a territory manager');
      return;
    }
  
    try {
      const response = await api.get(`/sites/site_supervision_pdf_for_manager/`, {
        params: { territory_manager: territoryManager },
        responseType: 'blob',
      });
  
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, `${territoryManager}_site_supervision_report.pdf`);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      message.error('Failed to generate PDF report for the selected territory manager');
    }
  };

  const showEditModal = (site) => {
    setEditingSite(site);
    editForm.setFieldsValue({
      ...site,
      site_coverage: site.site_coverage ?? 0,
      winter_start_date: site.winter_start_date ? moment(site.winter_start_date) : null,
      winter_end_date: site.winter_end_date ? moment(site.winter_end_date) : null,
      contract_expiration_date: site.contract_expiration_date ? moment(site.contract_expiration_date) : null,
    });
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingSite(null);
    setSelectedEditAddress('');
    setSelectedEditLatitude('');
    setSelectedEditLongitude('');
    editForm.resetFields();
  };

  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      
      // Check if address is in form values
      if (!values.address || !values.latitude || !values.longitude) {
        message.error('Please select a valid address.');
        return;
      }
      
      const formattedValues = {
        ...values,
        // client_name: clients.find((client) => client.client_id === values.client)?.client_name || null,
        parking_lots_sqft: parseFloat(values.parking_lots_sqft),
        walkway_sqft: parseFloat(values.walkway_sqft),
        site_coverage: parseInt(values.site_coverage, 10),
        base_revenue: parseFloat(values.base_revenue),
        winter_start_date: values.winter_start_date ? values.winter_start_date.format('YYYY-MM-DD') : null,
        winter_end_date: values.winter_end_date ? values.winter_end_date.format('YYYY-MM-DD') : null,
        contract_expiration_date: values.contract_expiration_date ? values.contract_expiration_date.format('YYYY-MM-DD') : null,
        monthly_rate: parseFloat(values.monthly_rate),
        additional_revenue_amount: parseFloat(values.additional_revenue_amount),
      };
      const mutation = (previewHash) => api.patch(
        `/sites/${editingSite.site_id}/`,
        {
          ...formattedValues,
          ...(previewHash ? { status_preview_hash: previewHash } : {}),
        }
      );
      const response = (
        editingSite.contract_status === 'Confirmed' &&
        formattedValues.contract_status !== 'Confirmed'
      )
        ? await runStatusChangeWithPreview({
          siteIds: [editingSite.site_id],
          targetStatus: formattedValues.contract_status,
          title: `Update ${editingSite.site_name}?`,
          mutate: mutation,
        })
        : await mutation(null);
      if (!response) return;
      message.success('Site updated successfully');
      setIsEditModalVisible(false);
      editForm.resetFields();
      fetchSites();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error('Failed to update site:', error);
      message.error('Failed to update site');
    }
  };

  const isAllocationPresent = (site) => {
    return site && (site.parking_lots_sqft_allocated > 0 || site.walkway_sqft_allocated > 0 || site.assigned_walkway_crew_bool || site.assigned_salting_route_bool);
  };

  const showClientDetailsModal = (site) => {
    setSelectedSite(site);
    setIsClientDetailsModalVisible(true);
  };

  const handleClientDetailsModalCancel = () => {
    setIsClientDetailsModalVisible(false);
    setSelectedSite(null);
  };

  const menu = (
    <Menu>
      <Menu.Item key="1" onClick={generateManagementPDF}>
        <IconFile size={16} style={{ marginRight: 8 }} />
        Generate Site Management PDF
      </Menu.Item>
      <Menu.Divider />
        <Menu.SubMenu key="2"       
          title={
            <span>
              <IconFile size={16} style={{ marginRight: 8 }} />
              Generate Territory Manager PDF
            </span>
          }>
          {Array.from(new Set((sites || []).map((site) => site.assigned_territory_manager)))
            .filter((manager) => manager) // Exclude null or undefined values
            .map((manager) => (
              <Menu.Item
                key={manager}
                onClick={() => generateManagerPDF(manager)}
              >
                {manager}
              </Menu.Item>
            ))
          }
        </Menu.SubMenu>
    </Menu>
  );

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/sites/export_csv/', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sites_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error('Failed to export sites data');
      console.error('Export error:', error);
    }
  };

  const handleEmailTM = async () => {
    try {
      // Fetch preview data
      const previewResponse = await api.get('/sites/email_territory_managers/', {
        params: { preview_only: true },
      });
  
      if (previewResponse.status === 200) {
        const { emailed_managers, failed_emails } = previewResponse.data;
  
        // Construct the list of emails to show in the modal
        const emailList = emailed_managers && emailed_managers.length
          ? emailed_managers.join(', ')
          : "No managers available to email.";
  
        const failureList = failed_emails && failed_emails.length
          ? failed_emails.map((fail) => `${fail.manager} (${fail.reason})`).join(', ')
          : "No issues detected.";
  
        // Open the modal and display the preview
        Modal.confirm({
          title: "Are you sure?",
          content: (
            <div>
              <p>This action will email all Territory Managers with the latest reports. Do you want to proceed?</p>
              <p><b>Emails to be sent to:</b> {emailList}</p>
            </div>
          ),
          okText: "Yes, Proceed",
          cancelText: "Cancel",
          onOk: async () => {
            try {
              const finalResponse = await api.get('/sites/email_territory_managers/');
              if (finalResponse.status === 200) {
                const { emailed_managers } = finalResponse.data;
  
                const successMessage = emailed_managers.length
                  ? `Emails sent successfully to: ${emailed_managers.join(', ')}`
                  : "No emails were sent successfully.";
  
                if (emailed_managers.length) message.success(successMessage, 10);
              } else {
                message.error("Failed to send emails to Territory Managers.");
              }
            } catch (error) {
              console.error("Error sending emails to Territory Managers:", error);
              message.error("Failed to send emails. Please try again.");
            }
          },
          onCancel: () => {
            message.info("Action cancelled.");
          },
        });
      } else {
        message.error("Failed to fetch preview data. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching preview data for Territory Managers:", error);
      message.error("An error occurred while fetching preview data. Please try again.");
    }
  };
  

  const handleEdit = (record) => {
    setEditingSite(record);
    setSelectedEditAddress(record.address || '');
    setSelectedEditLatitude(record.latitude?.toString() || '');
    setSelectedEditLongitude(record.longitude?.toString() || '');
    editForm.setFieldsValue({
      ...record,
      client: record.client_id,
      weather_station_id: record.weather_station?.id ?? null,
      winter_start_date: record.winter_start_date ? moment(record.winter_start_date) : null,
      winter_end_date: record.winter_end_date ? moment(record.winter_end_date) : null,
      contract_expiration_date: record.contract_expiration_date ? moment(record.contract_expiration_date) : null,
      latitude: record.latitude?.toString(),
      longitude: record.longitude?.toString(),
      parking_lots_sqft: record.parking_lots_sqft?.toString(),
      walkway_sqft: record.walkway_sqft?.toString(),
      site_coverage: record.site_coverage ?? 0,
      base_revenue: record.base_revenue?.toString(),
      monthly_rate: record.monthly_rate?.toString(),
      additional_revenue_amount: record.additional_revenue_amount?.toString(),
    });
    setIsEditModalVisible(true);
  };

  const handleGenerateInvoice = async (record) => {
    try {
      await api.post(`/sites/${record.site_id}/generate-invoice/`);
      message.success('Invoice generated successfully');
      fetchSites(); // Refresh the sites list
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      message.error('Failed to generate invoice');
    }
  };

  const handleDownloadPDF = async (record) => {
    try {
      const response = await api.get(`/sites/${record.site_id}/`);
      await generateSiteDetailsPDF(response.data || record);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      message.error('Failed to download PDF');
    }
  };

  // put this OUTSIDE the component so it’s created once
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });
  

  const autocompleteRef = useRef(null);
  const editAutocompleteRef = useRef(null);

  const initAutocomplete = (inputRef, initialValue = '') => {
    // Check if the placeholder element exists and hasn't been initialized
    if (window.google && inputRef.current && !inputRef.current.querySelector('gmp-place-autocomplete')) {
      const autocomplete = new window.google.maps.places.PlaceAutocompleteElement();
      autocomplete.value = initialValue;

      autocomplete.addEventListener('gmp-select', async (event) => {
        try {
          const place = event.placePrediction?.toPlace();
          if (place) {
            await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
            
            const formattedAddress = place.formattedAddress || place.displayName;
            const location = place.location;
            
            if (!location) return;
            
            const isEditMode = inputRef === editAutocompleteRef;
            
            if (isEditMode) {
              setSelectedEditAddress(formattedAddress);
              setSelectedEditLatitude(location.lat().toFixed(6));
              setSelectedEditLongitude(location.lng().toFixed(6));
              
              editForm.setFieldsValue({
                address: formattedAddress,
                latitude: location.lat().toFixed(6),
                longitude: location.lng().toFixed(6)
              });
            } else {
              setSelectedAddress(formattedAddress);
              setSelectedLatitude(location.lat().toFixed(6));
              setSelectedLongitude(location.lng().toFixed(6));
              
              form.setFieldsValue({
                address: formattedAddress,
                latitude: location.lat().toFixed(6),
                longitude: location.lng().toFixed(6)
              });
            }
          }
        } catch (error) {
          console.error('Error processing place selection:', error);
        }
      });

      // Clear the placeholder and append the new element
      inputRef.current.innerHTML = '';
      inputRef.current.appendChild(autocomplete);
    }
  };

  useEffect(() => {
    if (isLoaded && isModalVisible) {
      // Use a short timeout to ensure the modal DOM is ready
      setTimeout(() => initAutocomplete(autocompleteRef), 100);
    }
  }, [isLoaded, isModalVisible]);

  useEffect(() => {
    if (isLoaded && isEditModalVisible && editingSite) {
      setTimeout(() => initAutocomplete(editAutocompleteRef, editingSite.address), 100);
    }
  }, [isLoaded, isEditModalVisible, editingSite]);

  const archivedColumns = archiveTab === 'archived' ? [
    {
      title: 'Archived At',
      dataIndex: 'archived_at',
      key: 'archived_at',
      render: (value) => value ? moment(value).format('YYYY-MM-DD HH:mm') : '—',
    },
  ] : [];

  const tableColumns = [
    ...(viewMode === 'default' ? defaultColumns : contractDetailsColumns),
    ...archivedColumns,
  ];

  const rowSelection = archiveTab === 'active' ? {
    selectedRowKeys: selectedSiteIds,
    preserveSelectedRowKeys: true,
    onSelect: (record, selected) => {
      setSelectedSiteIds((currentIds) => (
        selected
          ? Array.from(new Set([...currentIds, record.site_id]))
          : currentIds.filter((siteId) => siteId !== record.site_id)
      ));
    },
    onSelectAll: (selected, selectedRows, changedRows) => {
      const changedSiteIds = changedRows.map((site) => site.site_id);
      setSelectedSiteIds((currentIds) => (
        selected
          ? Array.from(new Set([...currentIds, ...changedSiteIds]))
          : currentIds.filter((siteId) => !changedSiteIds.includes(siteId))
      ));
    },
  } : undefined;

  const selectAllFilteredSites = () => {
    setSelectedSiteIds(filteredSites.map((site) => site.site_id));
  };

  const openBulkEditModal = () => {
    bulkForm.resetFields();
    setIsBulkEditModalVisible(true);
  };

  const closeBulkEditModal = () => {
    if (isBulkUpdating) return;
    setIsBulkEditModalVisible(false);
    bulkForm.resetFields();
  };

  const applyBulkUpdate = async (updates, statusPreviewHash = null) => {
    setIsBulkUpdating(true);
    try {
      const response = await api.patch('/sites/bulk-update/', {
        site_ids: selectedSiteIds,
        updates,
        ...(statusPreviewHash ? { status_preview_hash: statusPreviewHash } : {}),
      });
      const updatedSites = Array.isArray(response.data?.sites) ? response.data.sites : [];
      const updatedSitesById = new Map(updatedSites.map((site) => [site.site_id, site]));

      setSites((currentSites) => sortSitesForManagement(
        currentSites.map((site) => updatedSitesById.get(site.site_id) || site)
      ));
      await fetchSites();
      message.success(
        response.data?.message || `${selectedSiteIds.length} sites updated successfully.`
      );
      setSelectedSiteIds([]);
      setIsBulkEditModalVisible(false);
      bulkForm.resetFields();
      return response;
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.preview) {
        throw error;
      }
      console.error('Failed to bulk update sites:', error);
      const errorData = error.response?.data;
      message.error(getApiErrorMessage(errorData) || 'Failed to bulk update sites');
      if (errorData?.unavailable_site_ids) {
        fetchSites();
      }
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkEditSubmit = async () => {
    try {
      const values = await bulkForm.validateFields();
      const updates = {};

      if (values.update_region) {
        updates.region = values.region.trim();
      }
      if (values.update_contract_status) {
        updates.contract_status = values.contract_status;
      }
      if (values.update_assigned_territory_manager) {
        updates.assigned_territory_manager = values.assigned_territory_manager;
      }

      if (Object.keys(updates).length === 0) {
        message.warning('Select at least one field to update.');
        return;
      }

      const fieldLabels = {
        region: 'Region',
        contract_status: 'Status',
        assigned_territory_manager: 'Territory Manager',
      };
      const changedFields = Object.keys(updates).map((field) => fieldLabels[field]).join(', ');

      const movesConfirmedSitesAway = (
        updates.contract_status &&
        updates.contract_status !== 'Confirmed' &&
        sites.some(
          (site) => (
            selectedSiteIds.includes(site.site_id) &&
            site.contract_status === 'Confirmed'
          )
        )
      );
      if (movesConfirmedSitesAway) {
        await runStatusChangeWithPreview({
          siteIds: selectedSiteIds,
          targetStatus: updates.contract_status,
          title: `Update ${selectedSiteIds.length} selected sites?`,
          mutate: (previewHash) => applyBulkUpdate(updates, previewHash),
        });
      } else {
        Modal.confirm({
          title: `Update ${selectedSiteIds.length} selected sites?`,
          content: `The following fields will be updated: ${changedFields}.`,
          okText: 'Apply changes',
          cancelText: 'Cancel',
          onOk: () => applyBulkUpdate(updates),
        });
      }
    } catch (error) {
      if (!error?.errorFields) {
        console.error('Failed to validate bulk site update:', error);
        message.error('Failed to validate bulk site update');
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Sites Management</h1>
          <p className={styles.subtitle}>View and manage all site locations</p>
        </div>
        <div className={styles.actionButtons}>
          <Tooltip title="Add New Site">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
              className={styles.iconButton}
            />
          </Tooltip>
          <Tooltip title="Export to CSV">
            <Button
              onClick={handleExportCSV}
              type="primary"
              icon={<FileExcelOutlined />}
              className={styles.iconButton}
            />
          </Tooltip>
          <Tooltip title="Email Territory Managers">
            <Button
              onClick={handleEmailTM}
              type="primary"
              icon={<MailOutlined />}
              className={styles.iconButton}
            />
          </Tooltip>
          <Tooltip title="Toggle View Mode">
            <Switch
              checkedChildren="Contract"
              unCheckedChildren="Default"
              onChange={(checked) => setViewMode(checked ? 'contract' : 'default')}
              style={{ backgroundColor: checked => checked ? '#034832' : undefined }}
            />
          </Tooltip>
        </div>
      </div>

      <div className={styles.searchSection}>
        <Input
          placeholder="Search site name"
          prefix={<SearchOutlined style={{ color: '#6B7280' }} />}
          value={siteSearchInput}
          onChange={(e) => setSiteSearchInput(e.target.value)}
          style={{ width: '100%', maxWidth: '250px' }}
        />
        <Input
          placeholder="Search client name"
          prefix={<SearchOutlined style={{ color: '#6B7280' }} />}
          value={clientSearchInput}
          onChange={(e) => setClientSearchInput(e.target.value)}
          style={{ width: '100%', maxWidth: '250px' }}
        />
        <Select
          placeholder="Region"
          value={searchRegionFilter}
          onChange={(value) => setSearchRegionFilter(value)}
          style={{ width: '200px' }}
          allowClear
        >
          {regionOptions
            .map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
          ))}
        </Select>
        <Select
          placeholder="Contract Status"
          value={searchStatusFilter}
          onChange={(value) => setSearchStatusFilter(value)}
          style={{ width: '200px' }}
          allowClear
        >
          {statusOptions
            .map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
          ))}
        </Select>
      </div>

      <Tabs
        activeKey={archiveTab}
        onChange={setArchiveTab}
        style={{ marginBottom: 12 }}
        items={[
          { key: 'active', label: 'Active' },
          { key: 'archived', label: 'Archived' },
        ]}
      />

      {archiveTab === 'active' && filteredSites.length > 0 && (
        <div className={styles.bulkActionBar}>
          <span className={styles.selectedCount}>
            {selectedSiteIds.length} {selectedSiteIds.length === 1 ? 'site' : 'sites'} selected
          </span>
          <div className={styles.bulkActionButtons}>
            <Tooltip
              title={`Select all ${filteredSites.length} filtered sites`}
            >
              <span>
                <Button
                  onClick={selectAllFilteredSites}
                >
                  Select all {filteredSites.length} sites
                </Button>
              </span>
            </Tooltip>
            <Button
              onClick={() => setSelectedSiteIds([])}
              disabled={selectedSiteIds.length === 0}
            >
              Clear selection
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={openBulkEditModal}
              disabled={selectedSiteIds.length === 0}
            >
              Bulk Edit
            </Button>
          </div>
        </div>
      )}

      <Spin
        spinning={isBulkUpdating}
        size="large"
        tip={isBulkUpdating ? 'Updating sites...' : undefined}
      >
        <div className={styles.tableContainer}>
          <Table
            dataSource={filteredSites}
            columns={tableColumns}
            rowKey="site_id"
            rowSelection={rowSelection}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
            pagination={{
              current: tablePagination.current,
              pageSize: tablePagination.pageSize,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} sites`,
            }}
          />
        </div>
      </Spin>

      <Modal
        title="Bulk Edit Sites"
        open={isBulkEditModalVisible}
        onCancel={closeBulkEditModal}
        onOk={handleBulkEditSubmit}
        okText="Review changes"
        confirmLoading={isBulkUpdating}
        okButtonProps={{
          disabled:
            !hasBulkFieldEnabled ||
            selectedSiteIds.length === 0,
        }}
        cancelButtonProps={{ disabled: isBulkUpdating }}
      >
        <p>
          Update selected fields for {selectedSiteIds.length}{' '}
          {selectedSiteIds.length === 1 ? 'site' : 'sites'}. Unchecked fields will not change.
        </p>
        <Form
          form={bulkForm}
          layout="vertical"
          initialValues={{
            update_region: false,
            update_contract_status: false,
            update_assigned_territory_manager: false,
          }}
        >
          <Form.Item name="update_region" valuePropName="checked">
            <Checkbox>Update Region</Checkbox>
          </Form.Item>
          <Form.Item
            name="region"
            label="Region"
            dependencies={['update_region']}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (!getFieldValue('update_region')) return Promise.resolve();
                  if (!value || !value.trim()) {
                    return Promise.reject('Please enter a region');
                  }
                  if (value.trim().length > 50) {
                    return Promise.reject('Region must be 50 characters or fewer');
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input disabled={!updateBulkRegion} maxLength={50} />
          </Form.Item>

          <Form.Item name="update_contract_status" valuePropName="checked">
            <Checkbox>Update Status</Checkbox>
          </Form.Item>
          <Form.Item
            name="contract_status"
            label="Status"
            dependencies={['update_contract_status']}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => (
                  getFieldValue('update_contract_status') && !value
                    ? Promise.reject('Please select a status')
                    : Promise.resolve()
                ),
              }),
            ]}
          >
            <Select disabled={!updateBulkStatus} placeholder="Select status">
              <Option value="Estimate">Estimate</Option>
              <Option value="Unconfirmed">Unconfirmed</Option>
              <Option value="Confirmed">Confirmed</Option>
            </Select>
          </Form.Item>

          <Form.Item name="update_assigned_territory_manager" valuePropName="checked">
            <Checkbox>Update Territory Manager</Checkbox>
          </Form.Item>
          <Form.Item
            name="assigned_territory_manager"
            label="Territory Manager"
            dependencies={['update_assigned_territory_manager']}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => (
                  getFieldValue('update_assigned_territory_manager') && !value
                    ? Promise.reject('Please select a territory manager')
                    : Promise.resolve()
                ),
              }),
            ]}
          >
            <Select
              disabled={!updateBulkTerritoryManager}
              showSearch
              placeholder="Select a territory manager"
              filterOption={(inputValue, option) =>
                (option?.label ?? '').toLowerCase().includes(inputValue.toLowerCase())
              }
              options={territoryManagers.map((manager) => ({
                value: manager.name,
                label: manager.name,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add New Site"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical" initialValues={{ site_coverage: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <Form.Item name="site_name" label="Site Name" rules={[{ required: true, message: 'Please enter the site name' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item
              name="client"
              label="Client"
              rules={[{ required: true, message: 'Please select a client or add a new one' }]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <Select
                placeholder="Select a client or add new"
                onChange={(value) => {
                  setIsAddingNewClient(value === 'new');
                  if (value !== 'new') {
                    // Clear new client form fields if switching back to existing client
                    form.setFieldsValue({
                      new_client_name: undefined,
                      contact_first_name: undefined,
                      contact_last_name: undefined,
                      contact_email: undefined,
                      contact_phone: undefined
                    });
                  }
                }}
              >
                <Option value="new" style={{ fontWeight: 'bold' }}>+ Add New Client</Option>
                <Option value="" disabled><Divider style={{ margin: '4px 0' }} /></Option>
                {(clients || []).map(({ client_id, client_name }) => (
                  <Option key={client_id} value={client_id}>
                    {client_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* New Client Form Fields */}
            {isAddingNewClient && (
              <>
                <Divider orientation="left">New Client Information</Divider>
                <Form.Item
                  name="new_client_name"
                  label="Client Name"
                  rules={[{ required: true, message: 'Please enter the client name' }]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="contact_first_name"
                  label="Contact First Name"
                  rules={[{ required: true, message: 'Please enter the contact first name' }]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="contact_last_name"
                  label="Contact Last Name"
                  rules={[{ required: true, message: 'Please enter the contact last name' }]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="contact_phone"
                  label="Contact Phone"
                  rules={[
                    { required: true, message: 'Please enter the contact phone number' },
                    {
                      pattern: /^\d{10}$/,
                      message: 'Please enter a valid 10-digit phone number (numbers only)'
                    }
                  ]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input 
                    placeholder="1234567890"
                    maxLength={10}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, '');
                      form.setFieldsValue({ contact_phone: value });
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="contact_email"
                  label="Contact Email"
                  rules={[
                    { required: true, message: 'Please enter the contact email' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input />
                </Form.Item>
              </>
            )}

            <Form.Item label="Address" required style={{ flex: '1 1 calc(50% - 8px)' }}>
              <div ref={autocompleteRef} />
            </Form.Item>
            <Form.Item name="address" rules={[{ required: true, message: 'Please select an address' }]} style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="latitude" rules={[{ required: true }]} hidden={true}>
              <Input />
            </Form.Item>
            <Form.Item name="longitude" rules={[{ required: true }]} hidden={true}>
              <Input />
            </Form.Item>

            <Form.Item name="region" label="Region" rules={[{ required: true, message: 'Please enter the region' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item 
              name="parking_lots_sqft" 
              label="Parking Lots Sqft" 
              rules={[
                { required: true, message: 'Please enter parking lots square footage' },
                { validator: validateNumber }
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} />
            </Form.Item>
            <Form.Item 
              name="walkway_sqft" 
              label="Walkway Sqft" 
              rules={[
                { required: true, message: 'Please enter walkway square footage' },
                { validator: validateNumber }
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} />
            </Form.Item>
            <Form.Item
              name="site_coverage"
              label="Site Coverage (%)"
              rules={[
                { required: true, message: 'Please enter the site coverage' },
                { validator: validateSiteCoverage },
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} max={100} step={1} precision={0} />
            </Form.Item>
            <Form.Item name="contract_status" label="Contract Status" rules={[{ required: true, message: 'Please select the contract status' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select placeholder="Select contract status">
                <Option value="Estimate">Estimate</Option>
                <Option value="Unconfirmed">Unconfirmed</Option>
                <Option value="Confirmed">Confirmed</Option>
              </Select>
            </Form.Item>
            <Form.Item name="base_revenue" label="Base Revenue" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="winter_start_date" label="Winter Start Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="winter_end_date" label="Winter End Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="contract_expiration_date" label="Contract Expiration Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="monthly_rate" label="Monthly Rate (No HST)" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="additional_revenue_type" label="Additional Revenue Type" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="additional_revenue_amount" label="Additional Revenue Amount" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
          </div>
          <Form.Item name="latitude" hidden={true}>
            <Input />
          </Form.Item>
          <Form.Item name="longitude" hidden={true}>
            <Input />
          </Form.Item>
          <Form.Item name="assigned_territory_manager" label="Territory Manager" style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Select
              showSearch
              allowClear
              onChange={(value) => form.setFieldsValue({ assigned_territory_manager: value || null })}
              options={territoryManagers.map((manager) => ({
                value: manager.name,
                label: manager.name
              }))}
              placeholder="Select Territory Manager"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="assigned_site_supervisor" label="Site Supervisor" style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Select
              showSearch
              allowClear
              onChange={(value) => editForm.setFieldsValue({ assigned_site_supervisor: value || null })} // Set to null when cleared
              options={staffList.map((staff) => ({ value: staff.name }))}
              placeholder="Select Site Supervisor"
              filterOption={(inputValue, option) =>
                option.value.toLowerCase().includes(inputValue.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="dispatch_notes" label="Dispatch Notes" style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSubmit} style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}>
              Add Site
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Site Modal */}
      <Modal
        title="Edit Site"
        open={isEditModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        width={800}
      >
        <Form form={editForm} onFinish={handleEditSubmit} layout="vertical" initialValues={{ site_coverage: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <Form.Item name="site_name" label="Site Name" rules={[{ required: true, message: 'Please enter the site name' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item
              name="client"
              label="Client"
              rules={[{ required: false, message: 'Please select a client' }]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <Select
                disabled
                placeholder="Select a client"
                options={(clients || []).map(({ client_id, client_name }) => ({
                  value: client_id,
                  label: client_name,
                }))}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item label="Address" required style={{ flex: '1 1 calc(50% - 8px)' }}>
              <div ref={editAutocompleteRef} />
            </Form.Item>
            <Form.Item name="address" rules={[{ required: true, message: 'Please select an address' }]} style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="latitude" rules={[{ required: true }]} hidden={true}>
              <Input />
            </Form.Item>
            <Form.Item name="longitude" rules={[{ required: true }]} hidden={true}>
              <Input />
            </Form.Item>
            <Form.Item name="region" label="Region" rules={[{ required: true, message: 'Please enter the region' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item 
              name="parking_lots_sqft" 
              label="Parking Lots Sqft" 
              rules={[
                { required: true, message: 'Please enter parking lots square footage' },
                { validator: validateNumber }
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} disabled={isAllocationPresent(editingSite)} 
              title={isAllocationPresent(editingSite) ? "Ensure site deallocations before editing" : ""}/>
            </Form.Item>
            <Form.Item 
              name="walkway_sqft" 
              label="Walkway Sqft" 
              rules={[
                { required: true, message: 'Please enter walkway square footage' },
                { validator: validateNumber }
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} disabled={isAllocationPresent(editingSite)} 
              title={isAllocationPresent(editingSite) ? "Ensure site deallocations before editing" : ""}/>
            </Form.Item>
            <Form.Item
              name="site_coverage"
              label="Site Coverage (%)"
              rules={[
                { required: true, message: 'Please enter the site coverage' },
                { validator: validateSiteCoverage },
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} max={100} step={1} precision={0} />
            </Form.Item>
            <Form.Item name="contract_status" label="Contract Status" rules={[{ required: true, message: 'Please select the contract status' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select placeholder="Select contract status">
                <Option value="Estimate">Estimate</Option>
                <Option value="Unconfirmed">Unconfirmed</Option>
                <Option value="Confirmed">Confirmed</Option>
              </Select>
            </Form.Item>
            <Form.Item name="base_revenue" label="Base Revenue" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="winter_start_date" label="Winter Start Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="winter_end_date" label="Winter End Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="contract_expiration_date" label="Contract Expiration Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="monthly_rate" label="Monthly Rate (No HST)" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="additional_revenue_type" label="Additional Revenue Type" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="additional_revenue_amount" label="Additional Revenue Amount" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="assigned_territory_manager" label="Territory Manager" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select
                showSearch
                allowClear
                onChange={(value) => editForm.setFieldsValue({ assigned_territory_manager: value || null })} // Set to null when cleared
                options={territoryManagers.map((manager) => ({
                  value: manager.name,
                  label: manager.name
                }))}
                placeholder="Select Territory Manager"
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="assigned_site_supervisor" label="Site Supervisor" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select
                showSearch
                allowClear
                onChange={(value) => editForm.setFieldsValue({ assigned_site_supervisor: value || null })} // Set to null when cleared
                options={staffList.map((staff) => ({ value: staff.name }))}
                placeholder="Select Site Supervisor"
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="viaesys_site_id" label="Viaesys Site ID" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="qbo_key_account_id" label="QBO Key Account ID" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="weather_station_id" label="Weather Station" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select
                showSearch
                allowClear
                onChange={(value) => editForm.setFieldsValue({ weather_station_id: value ?? null })}
                options={weatherStations.map((station) => ({
                  value: station.id,
                  label: `${station.city_name}, ${station.postal_code}`,
                }))}
                placeholder="Select Weather Station"
                filterOption={(inputValue, option) =>
                  (option?.label ?? '').toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="dispatch_notes" label="Dispatch Notes" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input.TextArea rows={4} />
            </Form.Item>
          </div>
          <Form.Item>
            <Button type="primary" onClick={handleEditSubmit} style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}>
              Update Site
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Client Details Modal */}
      <Modal
        title="Client Details"
        visible={isClientDetailsModalVisible}
        onCancel={handleClientDetailsModalCancel}
        footer={null}
        width={600}
      >
        {selectedSite && (
          <div>
            <p><strong>Client Name:</strong> {selectedSite.client_name}</p>
            <p><strong>Site Name:</strong> {selectedSite.site_name}</p>
            <p><strong>Address:</strong> {selectedSite.address}</p>
            <p><strong>Region:</strong> {selectedSite.region}</p>
            <p><strong>Contract Status:</strong> {selectedSite.contract_status}</p>
            <p><strong>Base Revenue:</strong> ${formatNumber(selectedSite.base_revenue)}</p>
            <p><strong>Winter Start Date:</strong> {selectedSite.winter_start_date ? moment(selectedSite.winter_start_date).format('YYYY-MM-DD') : 'N/A'}</p>
            <p><strong>Winter End Date:</strong> {selectedSite.winter_end_date ? moment(selectedSite.winter_end_date).format('YYYY-MM-DD') : 'N/A'}</p>
            <p><strong>Contract Expiration:</strong> {selectedSite.contract_expiration_date ? moment(selectedSite.contract_expiration_date).format('YYYY-MM-DD') : 'N/A'}</p>
            <p><strong>Monthly Rate (No HST):</strong> ${formatNumber(selectedSite.monthly_rate)}</p>
            <p><strong>Additional Revenue Type:</strong> {selectedSite.additional_revenue_type || 'N/A'}</p>
            <p><strong>Additional Revenue Amount:</strong> ${formatNumber(selectedSite.additional_revenue_amount)}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SitesListView;
